import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { api } from '@/api/client';
import type { components } from '@/api/schema';
import { HistoryPanel } from '@/components/documents';
import {
  ActionBar,
  ColumnToggle,
  DataGrid,
  DetailPane,
  SplitView,
  StatusBar,
  readStored,
  writeStored,
  type DesktopAction,
  type DetailTab,
  type GridColumn,
} from '@/components/desktop';
import {
  ErrorNote,
  Field,
  FormActions,
  FormGroup,
  FormPanel,
  SearchBar,
  Select,
  errorMessage,
  shortDate,
} from '@/components/ui';
import { useAuth } from '@/stores/auth';
import { confirmAction } from '@/stores/confirm';
import { toast } from '@/stores/toast';
import { RolePermissionsEditor, describeCode, useRegistry } from './PermissionGrid';
import { ACTION_LABEL } from './permissionWords';

type RoleRow = components['schemas']['RoleListItemDto'];
type Form = 'create' | 'edit' | 'clone' | 'permissions';

const HIDDEN_KEY = 'poultry.grid.iam-roles.hidden';

function useRole(roleId: string) {
  return useQuery({
    queryKey: ['roles', roleId],
    queryFn: () => api.get(`/api/v1/roles/${roleId}` as '/api/v1/roles/{id}'),
  });
}

/**
 * Roles & Permissions. Roles are data rather than code: nothing in the system
 * branches on a role name, so what a role can do is entirely the permissions
 * it holds. Laid out like every other list — the action bar works on the
 * chosen role, the details pane shows it, and the permission grid opens
 * full-page so it has room (.docs/019).
 */
export function Roles() {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [tab, setTab] = useState('details');
  const [form, setForm] = useState<Form | null>(null);
  const [hidden, setHidden] = useState<Set<string>>(() => new Set(readStored<string[]>(HIDDEN_KEY, ['code'])));

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['roles'] });

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['roles', 'list', showInactive],
    queryFn: () => api.get('/api/v1/roles', { includeInactive: showInactive ? 'true' : undefined }),
  });

  const setActive = useMutation({
    mutationFn: (v: { row: RoleRow; isActive: boolean }) => api.patch(`/api/v1/roles/${v.row.id}`, { isActive: v.isActive }),
    onSuccess: (_r, v) => {
      invalidate();
      toast.success(
        v.isActive
          ? `${v.row.nameEn} is on again. Everyone holding it can do what it allows.`
          : `${v.row.nameEn} is switched off. Its holders lose what it allows until it is switched on.`,
      );
    },
    onError: (e) => toast.error(errorMessage(e, 'Could not change the role')),
  });

  const remove = useMutation({
    mutationFn: (row: RoleRow) => api.delete<{ outcome: string; message: string }>(`/api/v1/roles/${row.id}`),
    onSuccess: (r, row) => {
      // Refresh the lists only: refreshing everything under ['roles'] would
      // also re-request the role just deleted and get a 404.
      setSelectedKey(null);
      queryClient.removeQueries({ queryKey: ['roles', row.id] });
      void queryClient.invalidateQueries({ queryKey: ['roles', 'list'] });
      void queryClient.invalidateQueries({ queryKey: ['roles'], exact: true });
      toast.success(r.message);
    },
    onError: (e) => toast.error(errorMessage(e, 'Could not delete the role')),
  });

  const all = data ?? [];
  const needle = search.trim().toLowerCase();
  const rows = needle
    ? all.filter(
        (r) =>
          r.code.toLowerCase().includes(needle) ||
          r.nameEn.toLowerCase().includes(needle) ||
          (r.description ?? '').toLowerCase().includes(needle),
      )
    : all;
  const selected = rows.find((r) => r.id === selectedKey) ?? null;

  function rowActions(row: RoleRow): DesktopAction[] {
    const open = (f: Form) => () => {
      setSelectedKey(row.id);
      setForm(f);
    };
    return [
      { label: 'What it can do', icon: 'checklist', shortcut: 'Enter', onSelect: open('permissions'), allowed: can('iam.role.read') },
      {
        label: 'Edit details',
        icon: 'edit',
        onSelect: open('edit'),
        allowed: can('iam.role.update'),
        enabled: !row.isSystem,
        reason: 'Built-in roles keep their name. Copy it to make your own version.',
      },
      { label: 'Copy', icon: 'repeat', onSelect: open('clone'), allowed: can('iam.role.create') },
      row.isActive
        ? {
            label: 'Switch off',
            icon: 'suspend',
            separatorBefore: true,
            allowed: can('iam.role.update'),
            onSelect: () => {
              setSelectedKey(row.id);
              void confirmAction({
                title: `Switch off ${row.nameEn}?`,
                message: `It can no longer be given to anyone, and the ${peopleText(row.userCount)} holding it lose what it allows until it is switched back on.`,
                confirmLabel: 'Switch off',
                danger: true,
              }).then((ok) => ok && setActive.mutate({ row, isActive: false }));
            },
          }
        : {
            label: 'Switch on',
            icon: 'reactivate',
            separatorBefore: true,
            allowed: can('iam.role.update'),
            onSelect: () => {
              setSelectedKey(row.id);
              void confirmAction({
                title: `Switch ${row.nameEn} back on?`,
                message: `The ${peopleText(row.userCount)} holding it can do what it allows again, straight away.`,
                confirmLabel: 'Switch on',
              }).then((ok) => ok && setActive.mutate({ row, isActive: true }));
            },
          },
      {
        label: 'Delete',
        icon: 'remove',
        danger: true,
        allowed: can('iam.role.delete'),
        enabled: !row.isSystem && row.userCount === 0,
        reason: row.isSystem
          ? 'Built-in roles cannot be deleted. Switch it off instead.'
          : `${peopleText(row.userCount)} ${row.userCount === 1 ? 'holds' : 'hold'} this role. Give them another role first.`,
        onSelect: () => {
          setSelectedKey(row.id);
          void confirmAction({
            title: `Delete ${row.nameEn}?`,
            message: 'This cannot be undone.',
            confirmLabel: 'Delete',
            danger: true,
          }).then((ok) => ok && remove.mutate(row));
        },
      },
    ];
  }

  const toolbarActions: DesktopAction[] = [
    {
      label: 'Create role',
      icon: 'add',
      primary: true,
      onSelect: () => setForm('create'),
      allowed: can('iam.role.create'),
    },
    ...(selected
      ? rowActions(selected)
      : [{ label: 'What it can do', onSelect: () => {}, allowed: can('iam.role.read'), enabled: false, reason: 'Choose a role first' }]),
  ];

  const activeForm = form && (form === 'create' || selected) ? form : null;
  if (activeForm) {
    const done = (message: string) => {
      invalidate();
      toast.success(message);
      setForm(null);
    };
    const close = () => setForm(null);
    return (
      <div className="workbench">
        <div className="workbench__main workbench__main--form">
          {activeForm === 'create' && (
            <CreateRoleForm
              roles={all.filter((r) => r.isActive)}
              onCancel={close}
              onCreated={(id, message) => {
                invalidate();
                toast.success(message);
                // Straight into the grid: a new role is only useful once it
                // allows something.
                setSelectedKey(id);
                setShowInactive(false);
                setSearch('');
                setForm('permissions');
              }}
            />
          )}
          {activeForm === 'edit' && selected && <RoleDetailsForm key={selected.id} role={selected} onCancel={close} onSaved={done} />}
          {activeForm === 'clone' && selected && <CloneRoleForm key={selected.id} role={selected} onCancel={close} onSaved={done} />}
          {activeForm === 'permissions' && selected && (
            <RolePermissionsEditor key={selected.id} roleId={selected.id} onCancel={close} onSaved={done} />
          )}
        </div>
      </div>
    );
  }

  const columns: Array<GridColumn<RoleRow>> = [
    { key: 'name', header: 'Role', width: 200, render: (r) => r.nameEn },
    { key: 'about', header: 'What it is for', width: 320, render: (r) => <span className="dim">{r.description ?? '—'}</span> },
    { key: 'users', header: 'People', align: 'end', width: 80, render: (r) => r.userCount },
    { key: 'perms', header: 'Things it allows', align: 'end', width: 130, render: (r) => r.permissionCount },
    { key: 'kind', header: 'Kind', width: 100, render: (r) => <span className="pill">{r.isSystem ? 'Built in' : 'Custom'}</span> },
    {
      key: 'status',
      header: 'Status',
      width: 120,
      render: (r) => (r.isActive ? <span className="pill">Active</span> : <span className="pill pill--alert">⚠ Switched off</span>),
    },
    { key: 'code', header: 'Code', width: 110, render: (r) => <span className="mono">{r.code}</span> },
  ];
  const visibleColumns = columns.filter((c) => !hidden.has(c.key));

  const tabs: DetailTab[] = selected
    ? [
        { id: 'details', label: 'Details', icon: 'details', content: <RoleDetails role={selected} /> },
        { id: 'permissions', label: 'What it can do', icon: 'checklist', content: <RolePermissionList roleId={selected.id} /> },
        { id: 'people', label: 'People', icon: 'people', content: <RoleHolders roleId={selected.id} /> },
        { id: 'compare', label: 'Compare', icon: 'columns', content: <CompareRoles role={selected} roles={all} /> },
        { id: 'history', label: 'History', icon: 'history', content: <HistoryPanel path={`/api/v1/roles/${selected.id}/history`} /> },
      ]
    : [];

  return (
    <div className="workbench">
      <SplitView
        id="iam-roles"
        direction="column"
        initial={340}
        firstLabel="role list"
        first={
          <div className="workbench__main">
            <div className="filterbar">
              <SearchBar value={search} onChange={setSearch} placeholder="Search roles by name or what they are for…" />
              <label className="settings__toggle">
                <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
                <span className="field__label">Show switched off</span>
              </label>
            </div>

            <ActionBar actions={toolbarActions}>
              <ColumnToggle
                columns={columns.map((c) => ({ key: c.key, label: c.header }))}
                hidden={hidden}
                onChange={(next) => {
                  setHidden(next);
                  writeStored(HIDDEN_KEY, [...next]);
                }}
              />
            </ActionBar>

            {isError && <ErrorNote message={error.message} />}

            <DataGrid
              id="iam-roles"
              label="Roles"
              columns={visibleColumns}
              rows={rows}
              rowKey={(r) => r.id}
              selectedKey={selectedKey}
              onSelect={setSelectedKey}
              onOpen={(r) => {
                setSelectedKey(r.id);
                if (can('iam.role.read')) setForm('permissions');
              }}
              actions={rowActions}
              loading={isPending}
              empty={needle ? 'No roles match your search.' : 'No roles yet.'}
            />
          </div>
        }
        second={
          <DetailPane
            tabs={tabs}
            activeTab={tab}
            onTabChange={setTab}
            empty="Choose a role to see what it allows."
            title={selected && <span>{selected.nameEn}</span>}
          />
        }
      />
      <StatusBar>
        <span>
          {rows.length} role{rows.length === 1 ? '' : 's'}
        </span>
        <span>{selected ? `Chosen: ${selected.nameEn}` : 'No role chosen'}</span>
      </StatusBar>
    </div>
  );
}

function peopleText(n: number): string {
  return n === 1 ? '1 person' : `${n} people`;
}

function DetailItem({ label, value }: { label: string; value: ReactNode }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </>
  );
}

function RoleDetails({ role }: { role: RoleRow }) {
  return (
    <dl className="detaillist">
      <DetailItem label="Name" value={role.nameEn} />
      <DetailItem label="Name (Urdu)" value={role.nameUr ? <span className="urdu">{role.nameUr}</span> : '—'} />
      <DetailItem label="What it is for" value={role.description ?? '—'} />
      <DetailItem label="Kind" value={role.isSystem ? 'Built in — comes with the system' : 'Custom — made here'} />
      <DetailItem label="Things it allows" value={role.permissionCount} />
      <DetailItem label="People holding it" value={role.userCount} />
      <DetailItem label="Status" value={role.isActive ? 'Active' : 'Switched off'} />
      <DetailItem label="Code" value={<span className="mono">{role.code}</span>} />
    </dl>
  );
}

/** What a role allows, read-only, in plain words. */
function RolePermissionList({ roleId }: { roleId: string }) {
  const role = useRole(roleId);
  const registry = useRegistry();

  if (role.isPending || registry.isPending) return <p className="muted">Loading…</p>;
  if (role.isError || registry.isError) return <ErrorNote message="Could not load this role's permissions." />;

  const held = new Set(role.data.permissions);
  const lines = registry.data
    .map((mod) => ({
      module: mod.label,
      resources: mod.resources
        .map((res) => ({
          label: res.label,
          actions: res.actions.filter((a) => held.has(`${mod.module}.${res.resource}.${a}`)),
        }))
        .filter((res) => res.actions.length > 0),
    }))
    .filter((m) => m.resources.length > 0);

  if (lines.length === 0) return <p className="muted">This role allows nothing yet. Open "What it can do" to add some.</p>;

  return (
    <dl className="detaillist">
      {lines.map((m) => (
        <DetailItem
          key={m.module}
          label={m.module}
          value={m.resources
            .map((r) => `${r.label}: ${r.actions.map((a) => ACTION_LABEL[a] ?? a).join(', ')}`)
            .join(' · ')}
        />
      ))}
    </dl>
  );
}

/** Who holds this role. */
function RoleHolders({ roleId }: { roleId: string }) {
  const holders = useQuery({
    queryKey: ['roles', roleId, 'holders'],
    queryFn: () => api.get(`/api/v1/roles/${roleId}/holders` as '/api/v1/roles/{id}/holders'),
  });
  if (holders.isPending) return <p className="muted">Loading…</p>;
  if (holders.isError) return <ErrorNote message="Could not load who holds this role." />;
  if (holders.data.length === 0) return <p className="muted">Nobody holds this role.</p>;
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Name</th>
          <th>User ID</th>
          <th>Status</th>
          <th>Last seen</th>
          <th>Given</th>
        </tr>
      </thead>
      <tbody>
        {holders.data.map((h) => (
          <tr key={h.id}>
            <td>{h.fullName}</td>
            <td className="mono mono--ascase">{h.userId}</td>
            <td>{h.status === 'ACTIVE' ? 'Active' : 'Switched off'}</td>
            <td className="dim">{h.lastSeenAt ? shortDate(h.lastSeenAt) : 'Never'}</td>
            <td className="dim">{shortDate(h.assignedAt)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** The difference between this role and another, in plain lines. */
function CompareRoles({ role, roles }: { role: RoleRow; roles: RoleRow[] }) {
  const [otherId, setOtherId] = useState('');
  const mine = useRole(role.id);
  const other = useQuery({
    queryKey: ['roles', otherId],
    queryFn: () => api.get(`/api/v1/roles/${otherId}` as '/api/v1/roles/{id}'),
    enabled: otherId !== '',
  });
  const registry = useRegistry();

  const options = roles.filter((r) => r.id !== role.id).map((r) => ({ value: r.id, label: r.nameEn }));
  const ready = mine.data && other.data && registry.data;
  const a = new Set(mine.data?.permissions ?? []);
  const b = new Set(other.data?.permissions ?? []);
  const onlyA = [...a].filter((c) => !b.has(c));
  const onlyB = [...b].filter((c) => !a.has(c));

  return (
    <div className="stack">
      <Select label="Compare with" value={otherId} onChange={setOtherId} options={options} prompt="Choose a role" />
      {otherId && !ready && <p className="muted">Loading…</p>}
      {ready && onlyA.length === 0 && onlyB.length === 0 && <p>They allow exactly the same things.</p>}
      {ready && onlyA.length > 0 && (
        <div>
          <h4>Only {role.nameEn} can</h4>
          <ul className="stacklist">
            {onlyA.map((c) => (
              <li key={c}>{describeCode(registry.data!, c).text}</li>
            ))}
          </ul>
        </div>
      )}
      {ready && onlyB.length > 0 && (
        <div>
          <h4>Only {other.data!.nameEn} can</h4>
          <ul className="stacklist">
            {onlyB.map((c) => (
              <li key={c}>{describeCode(registry.data!, c).text}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** A new role: from nothing, or a copy of one that is close. */
function CreateRoleForm({
  roles,
  onCancel,
  onCreated,
}: {
  roles: RoleRow[];
  onCancel: () => void;
  onCreated: (id: string, message: string) => void;
}) {
  const [form, setForm] = useState({ nameEn: '', nameUr: '', description: '', from: '' });
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        nameEn: form.nameEn.trim(),
        ...(form.nameUr.trim() ? { nameUr: form.nameUr.trim() } : {}),
        ...(form.description.trim() ? { description: form.description.trim() } : {}),
      };
      return form.from
        ? api.post<{ id: string; code: string; leftOut?: number }>(`/api/v1/roles/${form.from}/clone`, body)
        : api.post<{ id: string; code: string; leftOut?: number }>('/api/v1/roles', body);
    },
    onSuccess: (r) => {
      const source = roles.find((x) => x.id === form.from);
      onCreated(
        r.id,
        source
          ? `${form.nameEn.trim()} made from ${source.nameEn}.${r.leftOut ? ` ${r.leftOut} things were left out because you cannot do them yourself.` : ''} Now adjust what it can do.`
          : `${form.nameEn.trim()} made. Now tick what it can do.`,
      );
    },
  });

  return (
    <FormPanel title="Create a role" intro="Start from nothing, or from a copy of a role that is close to what you need.">
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <FormGroup title="Role" columns={2} card>
          <Field label="Name" required value={form.nameEn} onChange={(nameEn) => set({ nameEn })} placeholder="Regional Auditor" />
          <Field label="Name (Urdu)" urdu value={form.nameUr} onChange={(nameUr) => set({ nameUr })} />
          <Field
            label="What it is for"
            span="full"
            value={form.description}
            onChange={(description) => set({ description })}
            hint="Shown in the roles list and when choosing roles for someone"
          />
          <Select
            label="Start from"
            value={form.from}
            onChange={(from) => set({ from })}
            options={roles.map((r) => ({ value: r.id, label: `A copy of ${r.nameEn}` }))}
            placeholder="Nothing — I will tick everything"
          />
        </FormGroup>

        {save.isError && <ErrorNote message={errorMessage(save.error, 'Could not save')} />}

        <FormActions onCancel={onCancel}>
          <button type="submit" className="btn btn--primary" disabled={save.isPending || !form.nameEn.trim()}>
            {save.isPending ? 'Saving…' : 'Create and choose what it can do'}
          </button>
        </FormActions>
      </form>
    </FormPanel>
  );
}

/** A custom role's name and description. */
function RoleDetailsForm({ role, onCancel, onSaved }: { role: RoleRow; onCancel: () => void; onSaved: (message: string) => void }) {
  const [form, setForm] = useState({
    nameEn: role.nameEn,
    nameUr: role.nameUr ?? '',
    description: role.description ?? '',
  });
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/api/v1/roles/${role.id}`, {
        nameEn: form.nameEn.trim(),
        ...(form.nameUr.trim() ? { nameUr: form.nameUr.trim() } : {}),
        ...(form.description.trim() ? { description: form.description.trim() } : {}),
      }),
    onSuccess: () => onSaved(`${form.nameEn.trim()} updated.`),
  });

  return (
    <FormPanel title={`Edit ${role.nameEn}`}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <FormGroup title="Role" columns={2} card>
          <Field label="Name" required value={form.nameEn} onChange={(nameEn) => set({ nameEn })} />
          <Field label="Name (Urdu)" urdu value={form.nameUr} onChange={(nameUr) => set({ nameUr })} />
          <Field
            label="What it is for"
            span="full"
            value={form.description}
            onChange={(description) => set({ description })}
            hint="Shown in the roles list and when choosing roles for someone"
          />
        </FormGroup>

        {save.isError && <ErrorNote message={errorMessage(save.error, 'Could not save')} />}

        <FormActions onCancel={onCancel}>
          <button type="submit" className="btn btn--primary" disabled={save.isPending || !form.nameEn.trim()}>
            {save.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </FormActions>
      </form>
    </FormPanel>
  );
}

function CloneRoleForm({ role, onCancel, onSaved }: { role: RoleRow; onCancel: () => void; onSaved: (message: string) => void }) {
  const [nameEn, setNameEn] = useState(`${role.nameEn} (copy)`);

  const save = useMutation({
    mutationFn: () =>
      api.post<{ id: string; code: string; leftOut?: number }>(`/api/v1/roles/${role.id}/clone`, { nameEn: nameEn.trim() }),
    onSuccess: (r) =>
      onSaved(
        `${nameEn.trim()} made with the same permissions as ${role.nameEn}` +
          (r.leftOut ? `, except ${r.leftOut} you cannot do yourself.` : '.'),
      ),
  });

  return (
    <FormPanel
      title={`Copy ${role.nameEn}`}
      intro={`Copies what ${role.nameEn} can do into a new custom role — everything you can do yourself. Changing the copy never changes ${role.nameEn}, and the other way round.`}
    >
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <FormGroup title="New role" columns={2} card>
          <Field label="Name" required value={nameEn} onChange={setNameEn} />
        </FormGroup>

        {save.isError && <ErrorNote message={errorMessage(save.error, 'Could not copy the role')} />}

        <FormActions onCancel={onCancel}>
          <button type="submit" className="btn btn--primary" disabled={save.isPending || !nameEn.trim()}>
            {save.isPending ? 'Copying…' : 'Copy role'}
          </button>
        </FormActions>
      </form>
    </FormPanel>
  );
}
